import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { ChevronRight, ArrowLeft, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

// Country configuration for Cold Drawn Coils - Belgium and Netherlands only
const COUNTRY_CONFIG = {
  BE: {
    code: 'BE',
    name: { en: 'Belgium', es: 'Bélgica', fr: 'Belgique', de: 'Belgien', nl: 'België' },
    certification: 'B500A (B500BS)',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/2b/EU-Belgium.svg/1200px-EU-Belgium.svg.png',
  },
  NL: {
    code: 'NL',
    name: { en: 'Netherlands', es: 'Países Bajos', fr: 'Pays-Bas', de: 'Niederlande', nl: 'Nederland' },
    certification: 'B500A (B500BS)',
    mapImage: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/EU-Netherlands.svg/1200px-EU-Netherlands.svg.png',
  },
};

const COUNTRY_ORDER = ['BE', 'NL'];

// Cold Drawn Wire specifications - same for both countries
const COLD_DRAWN_PRODUCTS = [
  { diameter: 'Ø 8', tons: '2.5T', direction: { en: 'Anticlockwise', es: 'Antihorario', fr: 'Sens antihoraire', de: 'Gegen den Uhrzeigersinn', nl: 'Linksgedraaid' } },
  { diameter: 'Ø 10', tons: '2.5T', direction: { en: 'Anticlockwise', es: 'Antihorario', fr: 'Sens antihoraire', de: 'Gegen den Uhrzeigersinn', nl: 'Linksgedraaid' } },
  { diameter: 'Ø 12', tons: '2.5T', direction: { en: 'Anticlockwise', es: 'Antihorario', fr: 'Sens antihoraire', de: 'Gegen den Uhrzeigersinn', nl: 'Linksgedraaid' } },
];

const translations = {
  en: {
    pageTitle: 'Cold Drawn Coils',
    pageSubtitle: 'Reinforcing steel wire in coils',
    backToProducts: 'Back to Products',
    selectCountry: 'Select your market',
    viewProducts: 'View Products',
    certification: 'Certificate',
    productTable: 'Product Specifications',
    tableHeaders: {
      diameter: 'Diameter',
      tons: 'Tons',
      direction: 'Direction',
    },
    availableIn: 'Available in',
  },
  es: {
    pageTitle: 'Bobinas Trefiladas',
    pageSubtitle: 'Acero de refuerzo en bobinas',
    backToProducts: 'Volver a Productos',
    selectCountry: 'Selecciona tu mercado',
    viewProducts: 'Ver Productos',
    certification: 'Certificado',
    productTable: 'Especificaciones de Producto',
    tableHeaders: {
      diameter: 'Diámetro',
      tons: 'Toneladas',
      direction: 'Dirección',
    },
    availableIn: 'Disponible en',
  },
  fr: {
    pageTitle: 'Bobines Tréfilées',
    pageSubtitle: 'Acier d\'armature en bobines',
    backToProducts: 'Retour aux Produits',
    selectCountry: 'Sélectionnez votre marché',
    viewProducts: 'Voir les Produits',
    certification: 'Certificat',
    productTable: 'Spécifications Produit',
    tableHeaders: {
      diameter: 'Diamètre',
      tons: 'Tonnes',
      direction: 'Direction',
    },
    availableIn: 'Disponible en',
  },
  de: {
    pageTitle: 'Gezogene Drahtspulen',
    pageSubtitle: 'Bewehrungsstahl in Spulen',
    backToProducts: 'Zurück zu Produkten',
    selectCountry: 'Wählen Sie Ihren Markt',
    viewProducts: 'Produkte Anzeigen',
    certification: 'Zertifikat',
    productTable: 'Produktspezifikationen',
    tableHeaders: {
      diameter: 'Durchmesser',
      tons: 'Tonnen',
      direction: 'Richtung',
    },
    availableIn: 'Verfügbar in',
  },
  nl: {
    pageTitle: 'Getrokken Draadspoelen',
    pageSubtitle: 'Wapeningsstaal in spoelen',
    backToProducts: 'Terug naar Producten',
    selectCountry: 'Selecteer uw markt',
    viewProducts: 'Bekijk Producten',
    certification: 'Certificaat',
    productTable: 'Productspecificaties',
    tableHeaders: {
      diameter: 'Diameter',
      tons: 'Ton',
      direction: 'Richting',
    },
    availableIn: 'Beschikbaar in',
  },
};

// Country Block Component (B2B industrial style with map silhouette)
const CountryBlock = ({ country, isSelected, onClick, t, language }) => {
  const countryName = country.name[language] || country.name.en;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between p-4 border-2 transition-all ${
        isSelected 
          ? 'border-green-600 bg-green-50' 
          : 'border-slate-200 bg-white hover:border-green-400 hover:bg-slate-50'
      }`}
      data-testid={`country-block-${country.code}`}
    >
      <div className="flex items-center space-x-4">
        {/* Country Map Silhouette */}
        <div className="w-16 h-12 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
          <img 
            src={country.mapImage} 
            alt={countryName}
            className="w-full h-full object-contain p-1"
            onError={(e) => {
              e.target.style.display = 'none';
              const fallback = document.createElement('span');
              fallback.className = 'text-2xl font-bold text-slate-400';
              fallback.textContent = country.code;
              e.target.parentElement.appendChild(fallback);
            }}
          />
        </div>
        <div className="text-left">
          <h3 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight">
            {countryName}
          </h3>
          <div className="flex items-center space-x-2 mt-1">
            <Award className="h-4 w-4 text-green-600" />
            <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-0.5">
              {t.certification}: {country.certification}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center space-x-2 text-sm font-medium text-slate-600">
        <span className="hidden sm:inline">{t.viewProducts}</span>
        <ChevronRight className={`h-5 w-5 transition-transform ${isSelected ? 'rotate-90 text-green-600' : 'text-slate-400'}`} />
      </div>
    </button>
  );
};

// Main Page Component
export const ColdDrawnWire = () => {
  const { language } = useLanguage();
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  const t = translations[language] || translations.en;

  return (
    <div className="min-h-screen bg-slate-900 pt-16 md:pt-20" data-testid="cold-drawn-wire-page">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/products" 
            className="inline-flex items-center space-x-2 text-green-500 hover:text-green-400 mb-6 transition-colors"
            data-testid="back-to-products"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">{t.backToProducts}</span>
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-4">
            {t.pageTitle}
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-2xl">
            {t.pageSubtitle}
          </p>
        </div>
      </section>

      <div className="bg-slate-50">
      {/* Country Selection */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-lg font-bold text-slate-900 uppercase tracking-tight mb-4">
            {t.selectCountry}
          </h2>
          <div className="grid md:grid-cols-2 gap-3 max-w-2xl">
            {COUNTRY_ORDER.map((code) => (
              <CountryBlock
                key={code}
                country={COUNTRY_CONFIG[code]}
                isSelected={selectedCountry === code}
                onClick={() => setSelectedCountry(selectedCountry === code ? null : code)}
                t={t}
                language={language}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Product Table (only visible when country selected) */}
      {selectedCountry && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Country Header */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-slate-200">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-8 bg-slate-100 rounded overflow-hidden flex items-center justify-center">
                  <img 
                    src={COUNTRY_CONFIG[selectedCountry].mapImage} 
                    alt={COUNTRY_CONFIG[selectedCountry].name[language]}
                    className="w-full h-full object-contain p-1"
                  />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase tracking-tight">
                    {t.productTable}
                  </h2>
                  <p className="text-slate-500 text-sm">
                    {t.availableIn}: {COUNTRY_CONFIG[selectedCountry].name[language]}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-green-600" />
                <span className="text-sm font-bold text-green-700">
                  {t.certification}: {COUNTRY_CONFIG[selectedCountry].certification}
                </span>
              </div>
            </div>

            {/* Product Table */}
            <div className="bg-white border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-900 text-white text-xs uppercase tracking-wider">
                      <th className="p-4 text-left font-medium">{t.tableHeaders.diameter}</th>
                      <th className="p-4 text-center font-medium">{t.tableHeaders.tons}</th>
                      <th className="p-4 text-center font-medium">{t.tableHeaders.direction}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COLD_DRAWN_PRODUCTS.map((product, idx) => (
                      <tr 
                        key={product.diameter}
                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-green-50 transition-colors`}
                        data-testid={`product-row-${idx}`}
                      >
                        <td className="p-4 font-mono font-bold text-slate-900">
                          {product.diameter}
                        </td>
                        <td className="p-4 text-center font-mono text-sm text-slate-700">
                          {product.tons}
                        </td>
                        <td className="p-4 text-center font-mono text-sm text-slate-700">
                          {product.direction[language] || product.direction.en}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Prompt to select country when none selected */}
      {!selectedCountry && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Award className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 text-lg">{t.selectCountry}</p>
          </div>
        </section>
      )}
      </div>
    </div>
  );
};
